import { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, MessageCircle, Headphones, Users, Award, MessageSquare } from 'lucide-react';
import emailjs from '@emailjs/browser';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    loanType: '',
    loanAmount: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formHeaderRef = useRef(null); // Ref for "Quick Loan Enquiry" header

  useEffect(() => {
    emailjs.init("iguiOxu1SQS--KIzl");
    
    // Auto-scroll to Quick Loan Enquiry header on page load
    const timer = setTimeout(() => {
      if (formHeaderRef.current) {
        formHeaderRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Call Us',
      info: '1800-123-4567',
      description: 'Mon-Sat: 9AM-6:30PM',
      link: 'tel:1800-123-4567',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Us',
      info: 'dhanplusloans@gmail.com',
      description: 'Response within 24hrs',
      link: 'mailto:dhanplusloans@gmail.com',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Visit Us',
      info: 'Wave One Tower, Noida',
      description: 'Mon-Sat: 9AM-6:30PM',
      link: 'https://www.google.com/maps/search/?api=1&query=Wave+One+Tower+Sector+18+Noida',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'WhatsApp',
      info: 'Chat on WhatsApp',
      description: 'Available 24/7',
      link: 'https://wa.me/919876543210?text=Hi%20DhanPlus,%20I%20need%20help%20with%20loan%20inquiry',
      color: 'from-green-400 to-green-600',
      isWhatsApp: true
    }
  ];

  const features = [
    { icon: <Headphones className="w-5 h-5" />, text: '24/7 Customer Support' },
    { icon: <Users className="w-5 h-5" />, text: 'Expert Loan Advisors' },
    { icon: <Award className="w-5 h-5" />, text: 'Quick Response Time' },
    { icon: <CheckCircle className="w-5 h-5" />, text: 'Trusted by 50K+ Customers' }
  ];

  const formatIndianCurrency = (num) => {
    if (!num) return "";
    const x = num.toString();
    const lastThree = x.substring(x.length - 3);
    const otherNumbers = x.substring(0, x.length - 3);
    if (otherNumbers !== "") {
      return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
    }
    return lastThree;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "loanAmount") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.loanType.trim()) newErrors.loanType = 'Loan type is required';
    if (!formData.loanAmount.trim()) newErrors.loanAmount = 'Loan amount is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    const serviceID = "service_o3d745k";
    const templateID = "template_89bxboj";

    const submissionDate = new Date().toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      loan_type: formData.loanType,
      loan_amount: `₹${formatIndianCurrency(formData.loanAmount)}`,
      message: formData.message,
      submission_date: submissionDate,
      to_name: "DhanPlus Support Team"
    };

    emailjs.send(serviceID, templateID, templateParams)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setIsSubmitting(false);
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', loanType: '', loanAmount: '', message: '' });
      })
      .catch((error) => {
        console.error('FAILED...', error);
        setIsSubmitting(false);
        alert('Failed to send message. Please try again or contact us directly.');
      });
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 text-white text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Enquiry Submitted!</h2>
              <p className="text-green-100">We've received your loan enquiry and will get back to you soon</p>
            </div>

            <div className="p-8">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">Our team will review your enquiry within 2-4 hours</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">A loan expert will contact you via email or phone</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700">Check your spam folder if you don't see our response</p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="bg-gradient-to-r from-blue-800 to-blue-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-900 hover:to-blue-800 transition-all duration-200"
                >
                  Send Another Enquiry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 via-blue-700 to-blue-900 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h1>
          <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
            We're here to help you with your loan needs. Reach out to us anytime!
          </p>
          <div className="flex flex-wrap justify-center gap-3 px-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-full border border-white/20 text-xs">
                <span className="text-yellow-400">{feature.icon}</span>
                <span>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Contact Methods Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {contactMethods.map((method, index) => (
            <a
              key={index}
              href={method.link}
              target={method.isWhatsApp || method.title === 'Visit Us' ? "_blank" : "_self"}
              rel={method.isWhatsApp || method.title === 'Visit Us' ? "noopener noreferrer" : undefined}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-5 border border-gray-100 group hover:-translate-y-1"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${method.color} rounded-lg flex items-center justify-center text-white mb-3 group-hover:scale-105 transition-transform duration-300 ${method.isWhatsApp ? 'animate-pulse' : ''}`}>
                {method.icon}
              </div>
              <h3 className="font-bold text-gray-900 text-sm mb-1">{method.title}</h3>
              <p className="text-blue-800 font-semibold text-xs mb-1 break-words">{method.info}</p>
              <p className="text-gray-600 text-xs -mt-1">{method.description}</p>
              {method.isWhatsApp && (
                <div className="mt-2 bg-green-50 text-green-700 text-xs font-medium px-2 py-1 rounded-full inline-block">
                  Instant Response
                </div>
              )}
            </a>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 h-fit">
          {/* Contact Form */}
          <div className="lg:col-span-2 h-fit">
            <div className="bg-white shadow-xl rounded-xl p-6 md:p-8 border border-gray-100 h-fit">
              {/* Quick Loan Enquiry Header - TARGET SCROLL POSITION */}
              <div ref={formHeaderRef} className="flex items-center space-x-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 md:w-6 md:h-6 text-blue-800" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Quick Loan Enquiry</h2>
                  <p className="text-gray-600 text-xs md:text-sm">Get personalized loan solutions in minutes</p>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-3 md:space-y-4">
                  {/* Full Name */}
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                          errors.name
                            ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                            : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                        }`}
                      />
                    </div>
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1 flex items-center space-x-1">
                        <AlertCircle className="w-3 h-3" />
                        <span>{errors.name}</span>
                      </p>
                    )}
                  </div>

                  {/* Phone and Email in Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    {/* Phone Field */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="9876543210"
                          maxLength="10"
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                            errors.phone
                              ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                              : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                          }`}
                        />
                      </div>
                      {errors.phone && (
                        <p className="text-red-500 text-xs mt-1 flex items-center space-x-1">
                          <AlertCircle className="w-3 h-3" />
                          <span>{errors.phone}</span>
                        </p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                            errors.email
                              ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                              : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                          }`}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1 flex items-center space-x-1">
                          <AlertCircle className="w-3 h-3" />
                          <span>{errors.email}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Loan Type and Amount in Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    {/* Loan Type */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Loan Type <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <select
                          name="loanType"
                          value={formData.loanType}
                          onChange={handleInputChange}
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 appearance-none bg-white cursor-pointer ${
                            errors.loanType
                              ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                              : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                          }`}
                        >
                          <option value="">Select Type</option>
                          <option value="Home Loan">🏠 Home Loan</option>
                          <option value="Personal Loan">💼 Personal Loan</option>
                          <option value="Business Loan">🏢 Business Loan</option>
                          <option value="Car Loan">🚗 Car Loan</option>
                          <option value="Education Loan">📚 Education Loan</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      {errors.loanType && (
                        <p className="text-red-500 text-xs mt-1 flex items-center space-x-1">
                          <AlertCircle className="w-3 h-3" />
                          <span>{errors.loanType}</span>
                        </p>
                      )}
                    </div>

                    {/* Loan Amount */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Loan Amount <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-600 font-bold text-lg">₹</span>
                        </div>
                        <input
                          type="text"
                          name="loanAmount"
                          value={formatIndianCurrency(formData.loanAmount)}
                          onChange={handleInputChange}
                          placeholder="0"
                          className={`w-full pl-9 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                            errors.loanAmount
                              ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                              : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                          }`}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Enter amount in Indian Rupees</p>
                      {errors.loanAmount && (
                        <p className="text-red-500 text-xs mt-1 flex items-center space-x-1">
                          <AlertCircle className="w-3 h-3" />
                          <span>{errors.loanAmount}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Message Field */}
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Message <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <textarea
                        name="message"
                        rows="3"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your loan requirements..."
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 resize-none ${
                          errors.message
                            ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                            : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                        }`}
                      ></textarea>
                    </div>
                    {errors.message && (
                      <p className="text-red-500 text-xs mt-1 flex items-center space-x-1">
                        <AlertCircle className="w-3 h-3" />
                        <span>{errors.message}</span>
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-xl font-semibold hover:from-blue-900 hover:to-blue-800 focus:ring-4 focus:ring-blue-300 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        <span>Send Enquiry</span>
                      </>
                    )}
                  </button>

                  {/* Privacy Notice */}
                  <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1 pt-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Your information is secure and confidential
                  </p>
                </div>
              </form>

              {/* FAQ Option Only */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <a
                  href="http://localhost:3000/#faqs"
                  className="inline-flex items-center text-blue-800 hover:text-blue-900 text-sm font-semibold gap-1 group"
                >
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Have Questions? View FAQs
                </a>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4 lg:space-y-6 h-fit">
            {/* Need Urgent Help? */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-lg p-5 md:p-6 border border-orange-200 h-fit">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center space-x-2 text-sm md:text-base">
                <Phone className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <span>Need Urgent Help?</span>
              </h3>
              <p className="text-xs md:text-sm text-gray-700 mb-4">
                For urgent loan queries or support, call our helpline
              </p>
              <a
                href="tel:1800-123-4567"
                className="block w-full bg-orange-600 text-white text-center py-2.5 md:py-3 rounded-lg font-semibold hover:bg-orange-700 transition-all duration-200 text-sm"
              >
                Call Now: 1800-123-4567
              </a>
            </div>

            {/* Office Hours */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-lg p-5 md:p-6 border border-blue-200 flex-1 h-fit">
              <div className="flex items-center space-x-3 mb-4">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-blue-800 flex-shrink-0" />
                <h3 className="font-bold text-gray-900 text-sm md:text-base">Office Hours</h3>
              </div>
              <div className="space-y-2 text-xs md:text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Monday - Saturday</span>
                  <span className="font-semibold text-gray-900">9:00 AM - 6:30 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Sunday</span>
                  <span className="font-semibold text-red-600">Closed</span>
                </div>
              </div>
            </div>

            {/* Office Address */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-lg p-5 md:p-6 border border-green-200 flex-1 min-h-0 h-fit">
              <div className="flex items-center space-x-3 mb-4">
                <MapPin className="w-5 h-5 md:w-6 md:h-6 text-green-700 flex-shrink-0" />
                <h3 className="font-bold text-gray-900 text-sm md:text-base">Our Office</h3>
              </div>
              <div className="space-y-1 text-xs md:text-sm text-gray-700 mb-4">
                <p className="font-semibold text-gray-900">White & Brooks</p>
                <p>Office Suite# 18, 18th Floor</p>
                <p>Gold Tower, Wave One Tower</p>
                <p>L-2A, Pocket G, Sector 18</p>
                <p>Noida, Uttar Pradesh 201301</p>
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Wave+One+Tower+Sector+18+Noida"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-all duration-200 text-center text-xs md:text-sm"
              >
                Get Directions →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
